import { useState } from "react";
import { Link, NavLink, useNavigate } from "react-router-dom";
import { Globe, Lock, Menu, X } from "lucide-react";
import { Logo } from "@/components/Logo";
import { useAuth } from "@/context/AuthContext";
import { useLang } from "@/i18n";

const LINKS = [
  { to: "/", label: "Home", id: "home" },
  { to: "/chi-siamo", label: "Chi Siamo", id: "chi-siamo" },
  { to: "/servizi", label: "Servizi", id: "servizi" },
  { to: "/team", label: "Team", id: "team" },
  { to: "/insights", label: "Insights", id: "insights" },
  { to: "/contatti", label: "Contatti", id: "contatti" },
];

export function LangToggle() {
  const { lang, setLang } = useLang();
  return (
    <div className="flex items-center gap-1 rounded-full border border-white/15 p-1" data-testid="lang-toggle">
      <Globe size={13} className="ml-2 text-gold" />
      {["it", "en"].map((l) => (
        <button
          key={l}
          onClick={() => setLang(l)}
          data-testid={`lang-toggle-${l}`}
          className={`rounded-full px-3 py-1 text-xs font-semibold uppercase tracking-wider transition-colors duration-300 ${
            lang === l ? "bg-gold text-navy-950" : "text-slate-400 hover:text-gold"
          }`}
        >
          {l}
        </button>
      ))}
    </div>
  );
}

export default function Navbar() {
  const [open, setOpen] = useState(false);
  const { user } = useAuth();
  const { t } = useLang();
  const navigate = useNavigate();

  const linkClass = ({ isActive }) =>
    `text-sm tracking-wide transition-colors duration-300 hover:text-gold ${
      isActive ? "text-gold" : "text-slate-300"
    }`;

  return (
    <header className="fixed top-0 z-50 w-full border-b border-white/10 bg-[#060A12]/80 backdrop-blur-xl" data-testid="main-navbar">
      <div className="mx-auto flex h-20 max-w-7xl items-center justify-between px-5 sm:px-8">
        <Link to="/" data-testid="nav-logo" onClick={() => setOpen(false)}>
          <Logo />
        </Link>

        <nav className="hidden items-center gap-8 lg:flex" data-testid="nav-links-desktop">
          {LINKS.map((l) => (
            <NavLink key={l.id} to={l.to} className={linkClass} data-testid={`nav-link-${l.id}`}>
              {t(l.label)}
            </NavLink>
          ))}
        </nav>

        <div className="hidden items-center gap-3 lg:flex">
          <LangToggle />
          <button
            data-testid="nav-area-riservata-btn"
            onClick={() => navigate("/area-riservata")}
            className="flex items-center gap-2 rounded-full border border-gold/40 px-5 py-2.5 text-sm text-gold transition-all duration-300 hover:border-gold hover:bg-gold/10"
          >
            <Lock size={14} />
            {user ? t("Il tuo Portale") : t("Area Riservata")}
          </button>
          <button
            data-testid="nav-cta-consulenza"
            onClick={() => navigate("/contatti")}
            className="rounded-full bg-gold px-5 py-2.5 text-sm font-semibold text-navy-950 transition-all duration-300 hover:bg-gold-light"
          >
            {t("Consulenza Strategica")}
          </button>
        </div>

        <div className="flex items-center gap-3 lg:hidden">
          <LangToggle />
          <button
            className="text-slate-200"
            onClick={() => setOpen(!open)}
            data-testid="nav-mobile-toggle"
            aria-label="Menu"
          >
            {open ? <X size={26} /> : <Menu size={26} />}
          </button>
        </div>
      </div>

      {open && (
        <div className="border-t border-white/10 bg-[#060A12]/95 backdrop-blur-xl lg:hidden" data-testid="nav-mobile-menu">
          <nav className="flex flex-col gap-1 px-6 py-6">
            {LINKS.map((l) => (
              <NavLink
                key={l.id}
                to={l.to}
                onClick={() => setOpen(false)}
                className={({ isActive }) =>
                  `py-3 text-base ${isActive ? "text-gold" : "text-slate-300"}`
                }
                data-testid={`nav-mobile-link-${l.id}`}
              >
                {t(l.label)}
              </NavLink>
            ))}
            <button
              data-testid="nav-mobile-area-riservata-btn"
              onClick={() => {
                setOpen(false);
                navigate("/area-riservata");
              }}
              className="mt-4 flex items-center justify-center gap-2 rounded-full border border-gold/40 px-5 py-3 text-gold"
            >
              <Lock size={14} /> {t("Area Riservata")}
            </button>
          </nav>
        </div>
      )}
    </header>
  );
}
